import React, { useState, useEffect, useRef } from 'react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import "../components/table.css";
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Card,
  CardContent,
  IconButton,
  useTheme,
  useMediaQuery,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress,
  Skeleton,
  Stack,
  InputAdornment,
} from '@mui/material';
import {
  Search,
  FilterList,
  Visibility,
  Edit,
  Cancel,
  CheckCircle,
  Pending,
  Add,
  CalendarToday,
  People,
  Person,
  Phone,
  Download,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { Formik, Form, Field } from 'formik';
import * as Yup from 'yup';
import { bookingsAPI, sportsAPI, courtsAPI } from '../services/api';
import OutlinedInput from '@mui/material/OutlinedInput';

const BookingsList = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();

  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalBookings, setTotalBookings] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [sportFilter, setSportFilter] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [dateOpen, setDateOpen] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    bookingId: '',
    action: 'confirm'
  });
  const [alert, setAlert] = useState(null);
  const [sports, setSports] = useState([]);
  const [courts, setCourts] = useState([]);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [walkInDialog, setWalkInDialog] = useState({ open: false });
  const [selectedSlots, setSelectedSlots] = useState([]);
  const [walkInLoading, setWalkInLoading] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);

  useEffect(() => {
    fetchBookings();
  }, [page, rowsPerPage, statusFilter, sportFilter, searchTerm, dateFrom, dateTo]);

  useEffect(() => {
    fetchSports();
  }, []);

  const fetchCourts = async (sportId) => {
    try {
      const response = await courtsAPI.getAll({ sport_id: sportId });
      setCourts(response.data.data);
    } catch (error) {
      console.error('Error fetching courts:', error);
    }
  };

  const fetchAvailableSlots = async (sportId, date) => {
    try {
      setSlotsLoading(true);
      setAvailableSlots([]);
      const response = await bookingsAPI.getAvailableSlots(sportId, date);
      setAvailableSlots(response.data.data);
    } catch (error) {
      console.error('Error fetching slots:', error);
      setAvailableSlots([]);
    } finally {
      setSlotsLoading(false);
    }
  };

  const fetchSports = async () => {
    try {
      const response = await sportsAPI.getAll();
      setSports(response.data.data);
    } catch (error) {
      console.error('Error fetching sports:', error);
    }
  };

  const fetchBookings = async () => {
    try {
      setLoading(true);
      const params = {
        page: page + 1,
        limit: rowsPerPage,
        ...(statusFilter && { status: statusFilter }),
        ...(sportFilter && { sport: sportFilter }),
        ...(searchTerm && { search: searchTerm }),
        ...(dateFrom && { date_from: dateFrom }),
        ...(dateTo && { date_to: dateTo }),
      };

      const response = await bookingsAPI.getAll(params);
      setBookings(response.data.data.bookings);
      setTotalBookings(response.data.data.pagination.total);
    } catch (error) {
      console.error('Error fetching bookings:', error);
      setAlert({ type: 'error', message: 'Failed to load bookings' });
    } finally {
      setLoading(false);
    }
  };

  const handleExportCSV = async () => {
    setExportLoading(true);
    try {
      const params = {
        ...(statusFilter && { status: statusFilter }),
        ...(sportFilter && { sport: sportFilter }),
        ...(searchTerm && { search: searchTerm }),
        ...(dateFrom && { date_from: dateFrom }),
        ...(dateTo && { date_to: dateTo }),
      };

      const response = await bookingsAPI.exportCSV(params);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `bookings-${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      setAlert({ type: 'success', message: 'CSV exported successfully' });
    } catch (error) {
      console.error('Error exporting CSV:', error);
      setAlert({ type: 'error', message: 'Failed to export CSV' });
    } finally {
      setExportLoading(false);
    }
  };

  const handleStatusChange = async (bookingId, newStatus) => {
    try {
      await bookingsAPI.updateStatus(bookingId, newStatus);
      setAlert({ type: 'success', message: `Booking ${newStatus} successfully` });
      fetchBookings(); // Refresh the list
    } catch (error) {
      console.error('Error updating booking status:', error);
      setAlert({ type: 'error', message: 'Failed to update booking status' });
    }
  };

  const handleConfirmAction = () => {
    const { bookingId, action } = confirmDialog;
    handleStatusChange(bookingId, action === 'confirm' ? 'confirmed' : 'cancelled');
    setConfirmDialog({ open: false, bookingId: '', action: 'confirm' });
  };

  const handleWalkInSportChange = (setFieldValue, sportId, date) => {
    setFieldValue('sport_id', sportId);
    setFieldValue('court_id', '');
    setFieldValue('slots', []);
    setSelectedSlots([]);
    if (sportId) {
      fetchCourts(sportId);
      if (date) {
        fetchAvailableSlots(sportId, date);
      }
    } else {
      setCourts([]);
      setAvailableSlots([]);
    }
  };

  const handleWalkInDateChange = (setFieldValue, sportId, date) => {
    setFieldValue('date', date);
    setFieldValue('slots', []);
    setSelectedSlots([]);
    if (sportId && date) {
      fetchAvailableSlots(sportId, date);
    } else {
      setAvailableSlots([]);
    }
  };

  const handleWalkInSlotChange = (event, setFieldValue) => {
    const value = event.target.value;
    const selected = typeof value === 'string' ? value.split(',') : value;
    setSelectedSlots(selected);
    setFieldValue('slots', selected);
  };

  const handleWalkInSubmit = async (values, { resetForm }) => {
    setWalkInLoading(true);
    try {
      // Create a booking for each selected slot
      const slotResults = [];
      const failedSlots = [];
      
      const slotsToBook = values.slots && values.slots.length > 0 ? values.slots : selectedSlots;
      
      for (const slotKey of slotsToBook) {
        try {
          const [startTime, endTime] = slotKey.split('-');
          
          const bookingData = {
            sport_id: values.sport_id,
            court_id: values.court_id,
            date: values.date,
            start_time: startTime,
            end_time: endTime,
            customer_name: values.customer_name,
            customer_phone: values.customer_phone,
            notes: values.notes,
            players_count: values.players_count,
          };
          
          await bookingsAPI.createWalkIn(bookingData);
          slotResults.push(slotKey);
        } catch (slotError) {
          console.error('Error booking slot:', slotKey, slotError);
          failedSlots.push(slotKey);
        }
      }
      
      if (failedSlots.length > 0) {
        setAlert({ type: 'warning', message: `Booked ${slotResults.length} slot(s). Failed for ${failedSlots.length} slot(s)` });
      } else {
        setAlert({ type: 'success', message: `Walk-in booking created for ${slotResults.length} slot(s)` });
      }
      
      fetchBookings();
      setWalkInDialog({ open: false });
      resetForm();
      setCourts([]);
      setAvailableSlots([]);
      setSelectedSlots([]);
    } catch (error) {
      console.error('Error creating walk-in booking:', error);
      const errorMsg = error.response?.data?.error || 'Failed to create walk-in booking';
      setAlert({ type: 'error', message: errorMsg });
    } finally {
      setWalkInLoading(false);
    }
  };

  const openWalkInDialog = () => {
    setWalkInDialog({ open: true });
  };

  const closeWalkInDialog = () => {
    setWalkInDialog({ open: false });
    setCourts([]);
    setAvailableSlots([]);
    setSelectedSlots([]);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'confirmed':
      case 'paid':
        return 'success';
      case 'requested':
      case 'pending_on_arrival':
        return 'warning';
      case 'cancelled':
      case 'no_show':
        return 'error';
      default:
        return 'default';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'confirmed':
      case 'paid':
        return <CheckCircle />;
      case 'requested':
      case 'pending_on_arrival':
        return <Pending />;
      case 'cancelled':
      case 'no_show':
        return <Cancel />;
      default:
        return undefined;
    }
  };

  const formatDate = (dateString) => {
    // Handle various date formats and convert to yyyy-mm-dd
    const date = new Date(dateString);
    if (isNaN(date.getTime())) {
      // If invalid date, return original string
      return dateString;
    }
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const formatDateTime = (date, time) => {
    return `${formatDate(date)} ${time}`;
  };

  // Mobile card view for bookings
  const BookingCard = ({ booking }) => (
    <Card sx={{ mb: 2 }}>
      <CardContent>
        <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={1}>
          <Typography variant="h6">{booking.id}</Typography>
          <Chip
            icon={getStatusIcon(booking.status)}
            label={booking.status}
            color={getStatusColor(booking.status)}
            size="small"
          />
        </Box>

        <Typography variant="body2" color="text.secondary" gutterBottom>
          {booking.user_name}
        </Typography>

        <Grid container spacing={1} sx={{ mb: 2 }}>
          <Grid item xs={6}>
            <Typography variant="body2">
              <strong>Sport:</strong> {booking.sport_name}
            </Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="body2">
              <strong>Court:</strong> {booking.court_label}
            </Typography>
          </Grid>
          <Grid item xs={12}>
            <Typography variant="body2">
              <strong>Time:</strong> {formatDateTime(booking.date, `${booking.start_time}-${booking.end_time}`)}
            </Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="body2">
              <strong>Amount:</strong> ₹{booking.amount}
            </Typography>
          </Grid>
        </Grid>

        <Box display="flex" gap={1} flexWrap="wrap">
          <Button
            size="small"
            variant="outlined"
            startIcon={<Visibility />}
            onClick={() => navigate(`/bookings/${booking.id}`)}
          >
            View
          </Button>
          {['requested', 'confirmed', 'paid'].includes(booking.status) && (
            <>
              {booking.status === 'requested' && (
                <Button
                  size="small"
                  variant="contained"
                  color="success"
                  startIcon={<CheckCircle />}
                  onClick={() => setConfirmDialog({
                    open: true,
                    bookingId: booking.id,
                    action: 'confirm'
                  })}
                >
                  Confirm
                </Button>
              )}
              <Button
                size="small"
                variant="contained"
                color="error"
                startIcon={<Cancel />}
                onClick={() => setConfirmDialog({
                  open: true,
                  bookingId: booking.id,
                  action: 'cancel'
                })}
              >
                Cancel
              </Button>
            </>
          )}
        </Box>
      </CardContent>
    </Card>
  );

  return (
    <Box sx={{ width: '100%', p: 0 }}>
      <Box display="flex" justifyContent="flex-end" alignItems="center" mb={3} gap={1}>
          <Button
            variant="contained"
            color="primary"
            startIcon={<Add />}
            onClick={openWalkInDialog}
          >
            Create Walk-in Booking
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleExportCSV}
            disabled={exportLoading}
            startIcon={exportLoading ? <CircularProgress size={18} color="inherit" /> : <Download />}
          >
            {exportLoading ? 'Exporting...' : 'Export CSV'}
          </Button>
        </Box>

      {alert && (
        <Alert severity={alert.type} sx={{ mb: 2 }} onClose={() => setAlert(null)}>
          {alert.message}
        </Alert>
      )}

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3, width: '100%' }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              placeholder="Search by name..."
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setPage(0);
              }}
              InputProps={{
                startAdornment: <Search sx={{ mr: 1, color: 'text.secondary' }} />,
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Box sx={{ position: 'relative' }}>
              <TextField
                fullWidth
                placeholder="Select Date Range"
                value={
                  dateFrom && dateTo
                    ? `${dateFrom} - ${dateTo}`
                    : dateFrom
                    ? `From ${dateFrom}`
                    : ''
                }
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <CalendarToday sx={{ color: 'text.secondary' }} />
                    </InputAdornment>
                  ),
                }}
                sx={{ cursor: 'pointer', '& .MuiInputBase-input': { cursor: 'pointer' } }}
                onClick={() => setDateOpen(!dateOpen)}
                readOnly
              />
              {dateOpen && (
                <Box
                  sx={{
                    position: 'absolute',
                    top: '100%',
                    left: 0,
                    zIndex: 9999,
                    bgcolor: 'white',
                    borderRadius: 1,
                    boxShadow: 3,
                    mt: 0.5,
                    width: 'auto',
                    minWidth: 550,
                    whiteSpace: 'nowrap',
                  }}
                >
                  <DatePicker
                    inline
                    selectsRange
                    monthsShown={2}
                    startDate={dateFrom ? new Date(dateFrom) : null}
                    endDate={dateTo ? new Date(dateTo) : null}
                    onChange={(update) => {
                      if (update[0] && update[1]) {
                        const fmt = (d) => {
                          const y = d.getFullYear();
                          const m = String(d.getMonth() + 1).padStart(2, '0');
                          const day = String(d.getDate()).padStart(2, '0');
                          return `${y}-${m}-${day}`;
                        };
                        setDateFrom(fmt(update[0]));
                        setDateTo(fmt(update[1]));
                        setPage(0);
                      } else if (update[0]) {
                        const d = update[0];
                        const y = d.getFullYear();
                        const m = String(d.getMonth() + 1).padStart(2, '0');
                        const day = String(d.getDate()).padStart(2, '0');
                        setDateFrom(`${y}-${m}-${day}`);
                        setDateTo('');
                        setPage(0);
                      }
                    }}
                    maxDate={new Date()}
                    minDate={new Date(new Date().setDate(new Date().getDate() - 365))}
                  />
                  <Box sx={{ p: 1, display: 'flex', justifyContent: 'flex-end', gap: 1, borderTop: '1px solid #eee' }}>
                    <Button size="small" onClick={() => setDateOpen(false)}>
                      Done
                    </Button>
                    <Button
                      size="small"
                      color="error"
                      onClick={() => {
                        setDateFrom('');
                        setDateTo('');
                        setPage(0);
                        setDateOpen(false);
                      }}
                    >
                      Clear
                    </Button>
                  </Box>
                </Box>
              )}
            </Box>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth>
              <Select
                value={statusFilter}
                displayEmpty
                onChange={(e) => {
                  setStatusFilter(e.target.value);
                  setPage(0);
                }}
                renderValue={(value) => {
                  if (!value) return <Typography color="text.secondary">All Status</Typography>;
                  const statusConfig = {
                    requested: { label: 'Requested', color: 'warning' },
                    confirmed: { label: 'Confirmed', color: 'success' },
                    paid: { label: 'Paid', color: 'success' },
                    cancelled: { label: 'Cancelled', color: 'error' },
                    no_show: { label: 'No Show', color: 'error' },
                    pending_on_arrival: { label: 'Pending on Arrival', color: 'warning' },
                  };
                  const config = statusConfig[value] || { label: value, color: 'default' };
                  return <Chip label={config.label} color={config.color} size="small" />;
                }}
              >
                <MenuItem value="">All Status</MenuItem>
                <MenuItem value="requested">Requested</MenuItem>
                <MenuItem value="confirmed">Confirmed</MenuItem>
                <MenuItem value="paid">Paid</MenuItem>
                <MenuItem value="cancelled">Cancelled</MenuItem>
                <MenuItem value="no_show">No Show</MenuItem>
                <MenuItem value="pending_on_arrival">Pending on Arrival</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth>
              <Select
                value={sportFilter}
                displayEmpty
                onChange={(e) => {
                  setSportFilter(e.target.value);
                  setPage(0);
                }}
                renderValue={(value) => {
                  if (!value) return <Typography color="text.secondary">All Sports</Typography>;
                  return sports.find(s => String(s.id) === String(value))?.name || value;
                }}
              >
                <MenuItem value="">All Sports</MenuItem>
                {sports.map((sport) => (
                  <MenuItem key={sport.id} value={sport.id}>
                    {sport.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <Button
              fullWidth
              variant="outlined"
              startIcon={<FilterList />}
              onClick={() => {
                setSearchTerm('');
                setStatusFilter('');
                setSportFilter('');
                setDateFrom('');
                setDateTo('');
                setPage(0);
              }}
            >
              Clear
            </Button>
          </Grid>
        </Grid>
        {/* Active filter chips */}
        {(searchTerm || statusFilter || sportFilter || dateFrom || dateTo) && (
          <Box sx={{ mt: 2, display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              Active filters:
            </Typography>
            {searchTerm && (
              <Chip
                label={`Search: "${searchTerm}"`}
                size="small"
                onDelete={() => {
                  setSearchTerm('');
                  setPage(0);
                }}
              />
            )}
            {statusFilter && (
              <Chip
                label={`Status: ${statusFilter}`}
                size="small"
                onDelete={() => {
                  setStatusFilter('');
                  setPage(0);
                }}
              />
            )}
            {sportFilter && (
              <Chip
                label={`Sport: ${sports.find(s => s.id == sportFilter)?.name || sportFilter}`}
                size="small"
                onDelete={() => {
                  setSportFilter('');
                  setPage(0);
                }}
              />
            )}
            {dateFrom && (
              <Chip
                label={`From: ${dateFrom}`}
                size="small"
                onDelete={() => {
                  setDateFrom('');
                  setPage(0);
                }}
              />
            )}
            {dateTo && (
              <Chip
                label={`To: ${dateTo}`}
                size="small"
                onDelete={() => {
                  setDateTo('');
                  setPage(0);
                }}
              />
            )}
          </Box>
        )}
      </Paper>

{/* Desktop Table View */}
      {!isMobile && (
        <Paper sx={{ width: '100%', position: 'relative' }}>
          <TableContainer className="app-table-responsive">
            <Table className="app-table app-table-bordered app-table-light">
              <TableHead className="app-table-light">
                <TableRow>
                  <TableCell>Booking ID</TableCell>
                  <TableCell>User</TableCell>
                  <TableCell>Sport</TableCell>
                  <TableCell>Court</TableCell>
                  <TableCell>Date & Time</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="right">Amount</TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {bookings.length === 0 && !loading ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center">
                      <Box py={4}>
                        <Typography variant="h6" color="text.secondary" gutterBottom>
                          No bookings found
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {searchTerm || statusFilter || sportFilter || dateFrom || dateTo
                            ? 'Try adjusting your filters'
                            : 'No bookings yet. Create a walk-in booking to get started.'}
                        </Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                ) : (
                  bookings.map((booking) => (
                    <TableRow key={booking.id} hover>
                      <TableCell>{booking.id}</TableCell>
                      <TableCell>{booking.user_name}</TableCell>
                      <TableCell>{booking.sport_name}</TableCell>
                      <TableCell>{booking.court_label}</TableCell>
                      <TableCell>
                        {formatDateTime(booking.date, `${booking.start_time}-${booking.end_time}`)}
                      </TableCell>
                      <TableCell>
                        <Chip
                          icon={getStatusIcon(booking.status)}
                          label={booking.status}
                          color={getStatusColor(booking.status)}
                          size="small"
                        />
                      </TableCell>
                      <TableCell align="right">₹{booking.amount || 0}</TableCell>
                      <TableCell align="center">
                        <IconButton
                          size="small"
                          onClick={() => navigate(`/bookings/${booking.id}`)}
                          title="View Details"
                        >
                          <Visibility />
                        </IconButton>
                        {['requested', 'confirmed', 'paid'].includes(booking.status) && (
                          <>
                            {booking.status === 'requested' && (
                              <IconButton
                                size="small"
                                color="success"
                                onClick={() => setConfirmDialog({
                                  open: true,
                                  bookingId: booking.id,
                                  action: 'confirm'
                                })}
                                title="Confirm Booking"
                              >
                                <CheckCircle />
                              </IconButton>
                            )}
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => setConfirmDialog({
                                open: true,
                                bookingId: booking.id,
                                action: 'cancel'
                              })}
                              title="Cancel Booking"
                            >
                              <Cancel />
                            </IconButton>
                          </>
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          {loading && (
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: 'rgba(255, 255, 255, 0.7)',
                zIndex: 10,
              }}
            >
              <CircularProgress />
            </Box>
          )}
          <TablePagination
            component="div"
            count={totalBookings}
            page={page}
            onPageChange={(event, newPage) => setPage(newPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(event) => {
              setRowsPerPage(parseInt(event.target.value, 10));
              setPage(0);
            }}
          />
        </Paper>
      )}

      {/* Mobile Card View */}
      {isMobile && (
        <Box>
          {loading ? (
            [...Array(5)].map((_, i) => (
              <Card key={i} sx={{ mb: 2 }}>
                <CardContent>
                  <Skeleton height={30} width="60%" sx={{ mb: 1 }} />
                  <Skeleton height={20} width="40%" sx={{ mb: 2 }} />
                  <Skeleton height={20} width="100%" sx={{ mb: 1 }} />
                  <Skeleton height={20} width="80%" sx={{ mb: 1 }} />
                  <Skeleton height={20} width="50%" />
                </CardContent>
              </Card>
            ))
          ) : bookings.length === 0 ? (
            <Box py={6} textAlign="center">
              <Typography variant="h6" color="text.secondary" gutterBottom>
                No bookings found
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {searchTerm || statusFilter || sportFilter
                  ? 'Try adjusting your filters'
                  : 'No bookings yet. Create a walk-in booking to get started.'}
              </Typography>
            </Box>
          ) : (
            bookings.map((booking) => (
              <BookingCard key={booking.id} booking={booking} />
            ))
          )}
        </Box>
      )}

      {/* Confirmation Dialog */}
      <Dialog open={confirmDialog.open} onClose={() => setConfirmDialog({ open: false, bookingId: '', action: 'confirm' })}>
        <DialogTitle>
          {confirmDialog.action === 'confirm' ? 'Confirm Booking' : 'Cancel Booking'}
        </DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to {confirmDialog.action} this booking?
            {confirmDialog.action === 'cancel' && ' This action cannot be undone.'}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDialog({ open: false, bookingId: '', action: 'confirm' })}>
            Cancel
          </Button>
          <Button
            onClick={handleConfirmAction}
            variant="contained"
            color={confirmDialog.action === 'confirm' ? 'success' : 'error'}
          >
            {confirmDialog.action === 'confirm' ? 'Confirm' : 'Cancel Booking'}
          </Button>
        </DialogActions>
      </Dialog>

{/* Walk-in Booking Dialog */}
      <Dialog open={walkInDialog.open} onClose={closeWalkInDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          Create Walk-in Booking
        </DialogTitle>
        <Formik
          initialValues={{
            sport_id: '',
            court_id: '',
            date: new Date().toISOString().split('T')[0],
            customer_name: '',
            customer_phone: '',
            notes: '',
            players_count: 1,
            slots: [],
          }}
          validationSchema={Yup.object().shape({
            sport_id: Yup.string().required('Sport is required'),
            court_id: Yup.string().required('Court is required'),
            date: Yup.string().required('Date is required'),
            customer_name: Yup.string().required('Customer name is required'),
            customer_phone: Yup.string().required('Customer phone is required'),
          })}
          onSubmit={handleWalkInSubmit}
        >
          {({ errors, touched, values, setFieldValue }) => (
            <Form>
              <DialogContent>
                <FormControl fullWidth error={touched.sport_id && !!errors.sport_id} sx={{ mb: 2 }}>
                  <InputLabel shrink={true}>Sport</InputLabel>
                  <Select
                    value={values.sport_id}
                    label="Sport"
                    onChange={(e) => handleWalkInSportChange(setFieldValue, e.target.value, values.date)}
                  >
                    <MenuItem value="">Select Sport</MenuItem>
                    {sports.map((sport) => (
                      <MenuItem key={sport.id} value={sport.id}>
                        {sport.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <FormControl fullWidth error={touched.court_id && !!errors.court_id} sx={{ mb: 2 }}>
                  <InputLabel shrink={true}>Court</InputLabel>
                  <Select
                    value={values.court_id}
                    label="Court"
                    onChange={(e) => setFieldValue('court_id', e.target.value)}
                    disabled={!values.sport_id}
                  >
                    <MenuItem value="">Select Court</MenuItem>
                    {courts.map((court) => (
                      <MenuItem key={court.id} value={court.id}>
                        {court.court_label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <TextField
                  margin="dense"
                  fullWidth
                  label="Date"
                  name="date"
                  type="date"
                  error={touched.date && !!errors.date}
                  helperText={touched.date && errors.date}
                  value={values.date}
                  onChange={(e) => handleWalkInDateChange(setFieldValue, values.sport_id, e.target.value)}
                  sx={{ mb: 2 }}
                  InputLabelProps={{ shrink: true }}
                />

                <FormControl fullWidth error={touched.slots && !!errors.slots} sx={{ mb: 2 }}>
                  <InputLabel shrink={true}>Available Time Slots</InputLabel>
                  <Select
                    multiple
                    value={values.slots}
                    label="Available Time Slots"
                    onChange={(e) => handleWalkInSlotChange(e, setFieldValue)}
                    input={<OutlinedInput label="Available Time Slots" />}
                    disabled={!values.sport_id || !values.date || slotsLoading}
                    renderValue={(selected) => {
                      if (slotsLoading) return <CircularProgress size={18} />;
                      if (selected.length === 0) return <Typography color="text.secondary">Select time slots</Typography>;
                      return selected.map(key => {
                        const slot = availableSlots.find(s => `${s.startTime}-${s.endTime}` === key);
                        return slot ? slot.display : key;
                      }).join(', ');
                    }}
                  >
                    {slotsLoading ? (
                      <MenuItem value="" disabled>
                        <CircularProgress size={20} sx={{ mr: 1 }} /> Loading slots...
                      </MenuItem>
                    ) : availableSlots.filter(slot => slot.available).length === 0 ? (
                      <MenuItem value="" disabled>
                        No available slots for this date
                      </MenuItem>
                    ) : (
                      availableSlots.filter(slot => slot.available).map((slot) => (
                        <MenuItem key={`${slot.startTime}-${slot.endTime}`} value={`${slot.startTime}-${slot.endTime}`}>
                          <Chip
                            label={`${slot.display} (${slot.bookedCount}/${slot.totalCourts} booked)`}
                            size="small"
                            color={slot.bookedCount >= slot.totalCourts ? 'error' : slot.bookedCount > 0 ? 'warning' : 'success'}
                            sx={{ width: '100%' }}
                          />
                        </MenuItem>
                      ))
                    )}
                  </Select>
                </FormControl>

                <Field
                  as={TextField}
                  margin="dense"
                  fullWidth
                  label="Number of Players"
                  name="players_count"
                  type="number"
                  value={values.players_count}
                  inputProps={{ min: 1, max: 22 }}
                  sx={{ mb: 2 }}
                />

                <Field
                  as={TextField}
                  margin="dense"
                  fullWidth
                  label="Customer Name"
                  name="customer_name"
                  error={touched.customer_name && !!errors.customer_name}
                  helperText={touched.customer_name && errors.customer_name}
                  value={values.customer_name}
                  sx={{ mb: 2 }}
                />

                <Field
                  as={TextField}
                  margin="dense"
                  fullWidth
                  label="Customer Phone"
                  name="customer_phone"
                  type="tel"
                  error={touched.customer_phone && !!errors.customer_phone}
                  helperText={touched.customer_phone && errors.customer_phone}
                  value={values.customer_phone}
                  sx={{ mb: 2 }}
                />

                <Field
                  as={TextField}
                  margin="dense"
                  fullWidth
                  label="Notes (Optional)"
                  name="notes"
                  multiline
                  rows={2}
                  placeholder="Any special requests or notes..."
                  value={values.notes}
                />
              </DialogContent>
              <DialogActions>
                <Button onClick={closeWalkInDialog}>Cancel</Button>
                <Button type="submit" variant="contained">
                  Create Booking
                </Button>
              </DialogActions>
            </Form>
          )}
        </Formik>
      </Dialog>
    </Box>
  );
};

export default BookingsList;